import { useRef, useState } from "react";

interface ProjectMediaProps {
  image: string;
  video?: string;
  alt: string;
  /** Classes for the outer positioned wrapper (sizing/aspect-ratio usually lives on the parent instead) */
  containerClassName?: string;
  /** Classes applied to both the <img> and <video> - put hover/scale transforms here */
  mediaClassName?: string;
  /** Card behavior: video plays on mouse enter, pauses + resets on leave */
  playOnHover?: boolean;
  /** Hero behavior: video plays immediately and loops */
  autoPlay?: boolean;
  fit?: "cover" | "contain";
}

export function ProjectMedia({
  image,
  video,
  alt,
  containerClassName = "",
  mediaClassName = "",
  playOnHover = false,
  autoPlay = false,
  fit = "cover",
}: ProjectMediaProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(autoPlay);
  const fitClass = fit === "cover" ? "object-cover" : "object-contain";

  const handleEnter = () => {
    if (!playOnHover || !video) return;
    setIsPlaying(true);
    videoRef.current?.play().catch(() => {
      /* autoplay can be blocked before user interaction - the poster image stays visible */
    });
  };

  const handleLeave = () => {
    if (!playOnHover || !video) return;
    setIsPlaying(false);
    videoRef.current?.pause();
    if (videoRef.current) videoRef.current.currentTime = 0;
  };

  return (
    <div
      className={`relative h-full w-full overflow-hidden ${containerClassName}`}
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
    >
      <img
        src={image}
        alt={alt}
        loading="lazy"
        className={`h-full w-full ${fitClass} transition-opacity duration-500 ${mediaClassName} ${
          video && isPlaying ? "opacity-0" : "opacity-100"
        }`}
      />
      {video && (
        <video
          ref={videoRef}
          src={video}
          muted
          loop
          playsInline
          autoPlay={autoPlay}
          preload="metadata"
          className={`absolute inset-0 h-full w-full ${fitClass} transition-opacity duration-500 ${mediaClassName} ${
            isPlaying ? "opacity-100" : "opacity-0"
          }`}
        />
      )}
    </div>
  );
}